/**
 * Player avatar — the single hitbox + sprite-set + base feel the player rides.
 *
 * ONE fixed avatar (Isaac, 2026-07-01 polish): the old SMALL/BIG "forms" toggle is gone.
 * Physics variation is NOT player-dialed — it comes solely from the level's authored
 * ARCHETYPE (see Archetype.ts), whose profile is merged OVER this avatar's base config
 * (the same mergeConfig mechanism). So the avatar carries the base feel + the hitbox +
 * the manifest sprite set; the archetype supplies the per-style physics we (devs) tune.
 *
 * The type is kept (Player / World / Ghost read `.form.config / .body / .spriteSet /
 * .spriteScale`), just no longer a cycle.
 */
import { BASE_CONFIG, mergeConfig, type PhysicsConfig } from './PhysicsConfig';

export interface Form {
  /** Display + debug name. */
  name: string;
  /** Partial override merged over BASE_CONFIG to get this avatar's base feel. */
  physics: Partial<PhysicsConfig>;
  /** Collision AABB size in px (decoupled from sprite frame size). */
  body: { width: number; height: number };
  /** Logical manifest sprite-set prefix, e.g. "player_small" → player_small_idle. */
  spriteSet: string;
  /** Render scale applied to the (padded 128px) Kenney character frame. Live-tunable. */
  spriteScale: number;
}

/** A fully-resolved avatar: the merged config + the avatar metadata, ready to drive Player. */
export interface ResolvedForm {
  name: string;
  config: PhysicsConfig;
  body: { width: number; height: number };
  spriteSet: string;
  spriteScale: number;
}

/** The one and only player avatar — nimble base feel; the archetype layers its physics on top. */
export const PLAYER_AVATAR: Form = {
  name: 'player',
  physics: {}, // pure base feel; per-style physics comes from the archetype overlay
  body: { width: 44, height: 84 },
  spriteSet: 'player_small',
  spriteScale: 0.72,
};

/**
 * @deprecated Back-compat alias of {@link PLAYER_AVATAR}. Many unit tests build a bare
 * Player via `resolveForm(SMALL)`; this keeps them reading clearly now that the SMALL/BIG
 * form toggle is gone (there is one avatar). New code should use PLAYER_AVATAR.
 */
export const SMALL = PLAYER_AVATAR;

/** Resolve the avatar into its final config + metadata. The merge is the shared mechanism. */
export function resolveForm(form: Form): ResolvedForm {
  return {
    name: form.name,
    config: mergeConfig(BASE_CONFIG, form.physics),
    body: { ...form.body },
    spriteSet: form.spriteSet,
    spriteScale: form.spriteScale,
  };
}
